import type { HoverSegment, UUID } from '../../model/types';
import { SampleRunBuilder, type RunContext } from './PenCapture';
import type { RawSample } from './samples';

export const HOVER_MAX_MS = 2000;

export interface HoverDeps {
  /** Capture context for a hover segment starting at this sample, or null if not over a page. */
  contextAt(s: RawSample): RunContext | null;
  save(seg: Omit<HoverSegment, 'rev'>): void;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
}

/**
 * Records pen-in-range samples between strokes.
 * A segment ends at pen-down (linked to the new stroke), when the pen leaves
 * range, or HOVER_MAX_MS after it started without a stroke. After a timeout,
 * hover is ignored until the next stroke or until the pen leaves and returns.
 */
export class HoverRecorder {
  private seg: SampleRunBuilder | null = null;
  private prev: UUID | null = null;
  private blocked = false;
  private timer: unknown = null;
  private lastStroke: { id: UUID; pageId: UUID } | null = null;
  /** Count of hover pointer events seen (for the debug panel). */
  events = 0;
  segmentsSaved = 0;

  constructor(private deps: HoverDeps) {}

  onHover(samples: RawSample[]): void {
    this.events++;
    if (this.blocked || samples.length === 0) return;
    if (!this.seg) {
      const ctx = this.deps.contextAt(samples[0]!);
      if (!ctx) return;
      this.seg = new SampleRunBuilder(ctx);
      this.prev = this.lastStroke && this.lastStroke.pageId === ctx.pageId ? this.lastStroke.id : null;
      this.timer = this.deps.setTimer(() => this.timeout(), HOVER_MAX_MS);
    }
    for (const s of samples) this.seg.add(s);
  }

  /** Pen touched down; `strokeId` is the id the new stroke will have. */
  onPenDown(strokeId: UUID | null): void {
    this.close(strokeId);
    this.blocked = false;
  }

  onStrokeEnd(id: UUID, pageId: UUID): void {
    this.lastStroke = { id, pageId };
    this.blocked = false;
  }

  /** Pen left hover range. */
  onLeave(): void {
    this.close(null);
    this.blocked = false;
  }

  /** Saves any open segment (e.g. on page hide). */
  flush(): void {
    this.close(null);
  }

  get active(): boolean {
    return this.seg !== null;
  }

  private timeout(): void {
    this.timer = null;
    this.close(null);
    this.blocked = true;
  }

  private close(next: UUID | null): void {
    if (this.timer !== null) {
      this.deps.clearTimer(this.timer);
      this.timer = null;
    }
    const seg = this.seg;
    this.seg = null;
    if (!seg || seg.count === 0) return;
    this.deps.save(seg.toHover(this.prev, next));
    this.segmentsSaved++;
  }
}
